@file:OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class, ExperimentalFoundationApi::class)

package com.example.monetix.ui.screens

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateContentSize
import androidx.compose.animation.core.*
import androidx.compose.ui.graphics.Brush
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.basicMarquee
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.CheckCircle
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Payments
import androidx.compose.material3.*
import com.example.monetix.ui.components.MonetixTextField as OutlinedTextField
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.ui.draw.blur
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.example.monetix.db.AccountEntity
import com.example.monetix.db.TransactionEntity
import com.example.monetix.ui.components.ModernMoneyText
import com.example.monetix.ui.components.MonetixGlassSwitch
import com.example.monetix.utils.formatEuroFromCents
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.todayIn
import kotlin.math.abs
import kotlin.random.Random

// 🚀 ARCHITETTURA A DOPPIO BINARIO
const val SYS_DEBT_ACCOUNT_ID = "sys_debt_manager_account" // Il nucleo dei debiti
const val SYS_DEBT_REMINDER_ID = "sys_debt_reminder_account" // Il "Binario Fantasma" per i promemoria (Es. Dentista)
const val SYS_DEBT_CATEGORY_ID = "sys_debt_category"
const val SYS_DEBT_PLAN_CATEGORY = "sys_debt_plan"


@Composable
fun DebtManagerScreen(
    accounts: List<AccountEntity>,
    transactions: List<TransactionEntity>,
    onBack: () -> Unit,
    onSaveTransaction: (TransactionEntity) -> Unit,
    onDeleteTransaction: (TransactionEntity) -> Unit,
    onPopupStateChanged: (Boolean) -> Unit = {}
) {
    val brandPrimary = MaterialTheme.colorScheme.primary
    var creatingNew by remember { mutableStateOf(false) }

    // STATI DI ELIMINAZIONE / MODIFICA SINGOLA
    var transactionToDelete by remember { mutableStateOf<TransactionEntity?>(null) }
    var transactionToEdit by remember { mutableStateOf<TransactionEntity?>(null) }
    var txToMakeReal by remember { mutableStateOf<TransactionEntity?>(null) }

    // STATI PER IL RIMBORSO PARZIALE E PIANI
    var friendToRepay by remember { mutableStateOf<String?>(null) }
    var friendToIncrease by remember { mutableStateOf<String?>(null) }
    var isOwedToMeIncrease by remember { mutableStateOf(false) }
    var isOwedToMeRepay by remember { mutableStateOf(false) }
    var maxRepayAmount by remember { mutableStateOf(0L) }

    var suggestedRepayAmount by remember { mutableStateOf<Long?>(null) }

    // 🚀 STATI DEL PIANO DI RIENTRO
    var friendToCreatePlan by remember { mutableStateOf<String?>(null) }
    var planToEdit by remember { mutableStateOf<TransactionEntity?>(null) } // NOVITÀ: Modifica piano
    var planToCancel by remember { mutableStateOf<TransactionEntity?>(null) }

    // STATI GLOBALI PER ANAGRAFICA
    var friendToEditGlobal by remember { mutableStateOf<String?>(null) }
    var friendToDeleteGlobal by remember { mutableStateOf<String?>(null) }
    var selectedFriendForDetails by remember { mutableStateOf<String?>(null) }

    val isDebtPopupOpen = creatingNew || transactionToDelete != null || transactionToEdit != null ||
            txToMakeReal != null || friendToRepay != null || friendToIncrease != null ||
            friendToCreatePlan != null || planToEdit != null || planToCancel != null ||
            friendToEditGlobal != null || friendToDeleteGlobal != null

    val debtBlur by animateDpAsState(
        targetValue = if (isDebtPopupOpen) 12.dp else 0.dp,
        animationSpec = tween(durationMillis = 300),
        label = "debtBlur"
    )

    LaunchedEffect(isDebtPopupOpen) {
        onPopupStateChanged(isDebtPopupOpen)
    }

    val debtTransactions = remember(transactions) {
        transactions.filter { !it.isDeleted && (it.categoryId == SYS_DEBT_CATEGORY_ID || it.categoryId == SYS_DEBT_PLAN_CATEGORY) }
    }

    val friendsBalances = remember(debtTransactions) {
        val map = mutableMapOf<String, Long>()
        debtTransactions.forEach { tx ->
            if (tx.categoryId == SYS_DEBT_CATEGORY_ID) {
                val friendName = tx.merchantKey?.uppercase()?.trim() ?: "SCONOSCIUTO"

                val isGhost = tx.accountId?.startsWith("sys_ghost_") == true ||
                        tx.fromAccountId?.startsWith("sys_ghost_") == true ||
                        tx.toAccountId?.startsWith("sys_ghost_") == true ||
                        tx.accountId == "sys_ghost_tracker_account"

                if (!isGhost) {
                    val isMoneyLentToFriend = tx.toAccountId == SYS_DEBT_ACCOUNT_ID
                    val impact = if (isMoneyLentToFriend) tx.amountCents else -tx.amountCents
                    map[friendName] = (map[friendName] ?: 0L) + impact
                } else {
                    if (!map.containsKey(friendName)) map[friendName] = 0L
                }
            }
        }
        map.entries.toList().sortedByDescending { abs(it.value) }
    }

    val totalOwedToMe = friendsBalances.filter { it.value > 0 }.sumOf { it.value }
    val totalIOwe = friendsBalances.filter { it.value < 0 }.sumOf { abs(it.value) }

    // 🚀 SFONDO ANIMATO REVOLUT
    val infiniteTransition = rememberInfiniteTransition()
    val bgOffset by infiniteTransition.animateFloat(
        initialValue = 0f,
        targetValue = 1000f,
        animationSpec = infiniteRepeatable(
            animation = tween(20000, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse
        ),
        label = "bg_offset"
    )

    val hasCustomBg = com.example.monetix.LocalHasCustomBackground.current

    Box(
        modifier = Modifier
            .fillMaxSize()
            .then(
                if (hasCustomBg) Modifier.background(Color.Transparent)
                else Modifier.background(
                    Brush.verticalGradient(
                        colors = listOf(
                            Color(0xFF4F46E5).copy(alpha = 0.8f),
                            Color(0xFF1E1B4B),
                            Color(0xFF0F172A)
                        ),
                        startY = 0f,
                        endY = bgOffset + 1500f
                    )
                )
            )
            .blur(debtBlur)
    ) {
        Scaffold(
            containerColor = Color.Transparent
        ) { padding ->
        // Invece di usare il padding nativo, usiamo window insets statusBars se l'header globale è trasparente
        Column(modifier = Modifier.fillMaxSize().padding(padding)) {

            Surface(
                modifier = Modifier.fillMaxWidth(),
                color = Color.Transparent
            ) {
                Column(modifier = Modifier.padding(16.dp)) {

                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            IconButton(onClick = onBack) { Icon(Icons.Default.ArrowBack, "Indietro", tint = Color.White) }
                            Text("Ti devo / Mi devi", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold), color = Color.White)
                        }

                        Button(
                            onClick = { creatingNew = true },
                            colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.15f), contentColor = Color.White),
                            contentPadding = PaddingValues(horizontal = 12.dp, vertical = 0.dp),
                            modifier = Modifier.height(36.dp),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(16.dp))
                            Spacer(Modifier.width(4.dp))
                            Text("NUOVO", fontWeight = FontWeight.Bold, fontSize = 12.sp)
                        }
                    }

                    Spacer(Modifier.height(16.dp))

                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        Surface(modifier = Modifier.weight(1f), shape = RoundedCornerShape(16.dp), color = Color(0xFF10B981).copy(alpha = 0.15f), border = BorderStroke(1.dp, Color(0xFF10B981).copy(alpha = 0.3f))) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                Text("DA RICEVERE", style = MaterialTheme.typography.labelSmall, color = Color(0xFF34D399))
                                ModernMoneyText(cents = totalOwedToMe, color = Color(0xFF34D399), integerFontSize = 22.sp, decimalFontSize = 14.sp, symbolFontSize = 14.sp, modifier = Modifier.basicMarquee())
                            }
                        }
                        Surface(modifier = Modifier.weight(1f), shape = RoundedCornerShape(16.dp), color = MaterialTheme.colorScheme.error.copy(alpha = 0.15f), border = BorderStroke(1.dp, MaterialTheme.colorScheme.error.copy(alpha = 0.3f))) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                Text("DA DARE", style = MaterialTheme.typography.labelSmall, color = Color(0xFFF87171))
                                ModernMoneyText(cents = totalIOwe, color = Color(0xFFF87171), integerFontSize = 22.sp, decimalFontSize = 14.sp, symbolFontSize = 14.sp, modifier = Modifier.basicMarquee())
                            }
                        }
                    }
                }
            }

            LazyColumn(
                modifier = Modifier.weight(1f).padding(horizontal = 16.dp),
                contentPadding = PaddingValues(top = 16.dp, bottom = 80.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                if (friendsBalances.isEmpty()) {
                    item {
                        Box(Modifier.fillMaxWidth().height(200.dp), contentAlignment = Alignment.Center) {
                            Text("Nessun debito in corso. Siete tutti pari!", color = Color.White.copy(alpha = 0.6f))
                        }
                    }
                } else {
                    items(friendsBalances) { (friend, balance) ->
                        val isSettled = balance == 0L
                        val isOwedToMe = balance > 0
                        val isExpanded = selectedFriendForDetails == friend

                        val friendTxs = debtTransactions.filter { it.merchantKey?.uppercase() == friend }.sortedByDescending { it.createdAtEpochMs }
                        val visualTxs = friendTxs.filter { it.categoryId != SYS_DEBT_PLAN_CATEGORY }

                        val hasPending = visualTxs.any { it.accountId == SYS_DEBT_REMINDER_ID || it.fromAccountId == SYS_DEBT_REMINDER_ID || it.toAccountId == SYS_DEBT_REMINDER_ID }

                        val statusText = if (isSettled) {
                            if (hasPending) "In attesa di azioni" else "Saldato"
                        } else if (isOwedToMe) "Ti deve" else "Gli devi"

                        val color = if (isSettled) {
                            if (hasPending) Color(0xFFF59E0B) else MaterialTheme.colorScheme.onSurfaceVariant
                        } else if (isOwedToMe) Color(0xFF10B981) else MaterialTheme.colorScheme.error

                        val totalLent = visualTxs.filter { it.toAccountId == SYS_DEBT_ACCOUNT_ID }.sumOf { it.amountCents }
                        val totalBorrowed = visualTxs.filter { it.fromAccountId == SYS_DEBT_ACCOUNT_ID }.sumOf { it.amountCents }

                        val totalOriginal = if (isOwedToMe) totalLent else totalBorrowed
                        val totalRepaid = if (isOwedToMe) totalBorrowed else totalLent
                        val progressRaw = if (totalOriginal > 0) (totalRepaid.toFloat() / totalOriginal.toFloat()).coerceIn(0f, 1f) else 0f
                        val animatedProgress by animateFloatAsState(targetValue = progressRaw, label = "Progress")

                        Surface(
                            modifier = Modifier
                                .fillMaxWidth()
                                .animateContentSize()
                                .clickable(
                                    interactionSource = remember { androidx.compose.foundation.interaction.MutableInteractionSource() },
                                    indication = null,
                                    onClick = { selectedFriendForDetails = if (isExpanded) null else friend }
                                ),
                            shape = RoundedCornerShape(16.dp),
                            color = Color.White.copy(alpha = 0.08f), // 🚀 Sfondo vetro scuro
                            border = BorderStroke(1.dp, Color.White.copy(alpha = 0.1f))
                        ) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth()) {
                                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.weight(1f).padding(end = 8.dp)) {
                                        Box(modifier = Modifier.size(40.dp).background(color.copy(alpha = 0.15f), CircleShape), contentAlignment = Alignment.Center) {
                                            Text(friend.take(1).uppercase(), style = MaterialTheme.typography.titleMedium, color = color)
                                        }
                                        Spacer(Modifier.width(12.dp))
                                        Column(modifier = Modifier.weight(1f)) {
                                            Row(verticalAlignment = Alignment.CenterVertically) {
                                                Text(
                                                    text = friend,
                                                    style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.Bold),
                                                    color = Color.White,
                                                    maxLines = 1,
                                                    modifier = Modifier.weight(1f, fill = false).basicMarquee()
                                                )
                                                AnimatedVisibility(visible = isExpanded) {
                                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                                        IconButton(onClick = { friendToEditGlobal = friend }, modifier = Modifier.size(28.dp).padding(start = 4.dp)) {
                                                            Icon(Icons.Outlined.Edit, contentDescription = "Modifica Anagrafica", modifier = Modifier.size(16.dp), tint = Color.White.copy(alpha=0.7f))
                                                        }
                                                        IconButton(onClick = { friendToDeleteGlobal = friend }, modifier = Modifier.size(28.dp).padding(start = 4.dp)) {
                                                            Icon(Icons.Outlined.Delete, contentDescription = "Elimina Intero Debito", modifier = Modifier.size(16.dp), tint = Color(0xFFF87171))
                                                        }
                                                    }
                                                }
                                            }
                                            Text(statusText, style = MaterialTheme.typography.labelSmall, color = color)
                                        }
                                    }

                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        ModernMoneyText(cents = abs(balance), hideBalances = false, color = color, integerFontSize = 18.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                                        Icon(if(isExpanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore, null, tint = Color.White.copy(alpha=0.5f))
                                    }
                                }

                                AnimatedVisibility(visible = isExpanded) {
                                    Column(modifier = Modifier.padding(top = 16.dp)) {

                                        if (totalOriginal > 0) {
                                            HorizontalDivider(color = Color.White.copy(alpha = 0.1f))
                                            Spacer(Modifier.height(12.dp))
                                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                                Row(verticalAlignment = Alignment.Bottom) {
                                                    Text("Saldato: ", style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.7f), modifier = Modifier.padding(bottom = 1.dp))
                                                    ModernMoneyText(cents = totalRepaid, color = Color.White, integerFontSize = 12.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp)
                                                }
                                                Row(verticalAlignment = Alignment.Bottom) {
                                                    Text("Totale: ", style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.7f), modifier = Modifier.padding(bottom = 1.dp))
                                                    ModernMoneyText(cents = totalOriginal, color = Color.White, integerFontSize = 12.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp)
                                                }
                                            }
                                            Spacer(Modifier.height(6.dp))
                                            LinearProgressIndicator(
                                                progress = { animatedProgress },
                                                modifier = Modifier.fillMaxWidth().height(6.dp).clip(CircleShape),
                                                color = color,
                                                trackColor = color.copy(alpha = 0.2f)
                                            )
                                            Spacer(Modifier.height(16.dp))
                                        }

                                        HorizontalDivider(color = Color.White.copy(alpha = 0.1f))
                                        Spacer(Modifier.height(8.dp))

                                        visualTxs.forEach { tx ->
                                            val isMoneyFlowingToFriend = tx.toAccountId == SYS_DEBT_ACCOUNT_ID
                                            val isVirtual = tx.accountId == SYS_DEBT_REMINDER_ID || tx.fromAccountId == SYS_DEBT_REMINDER_ID || tx.toAccountId == SYS_DEBT_REMINDER_ID
                                            val isRepayment = tx.subcategoryText?.contains("Rimborso", ignoreCase = true) == true || tx.subcategoryText?.contains("Aggiornamento", ignoreCase = true) == true

                                            val txDate = try {
                                                val d = LocalDate.fromEpochDays(tx.dateEpochDay.toInt())
                                                "${d.dayOfMonth.toString().padStart(2, '0')}/${d.monthNumber.toString().padStart(2, '0')}/${d.year}"
                                            } catch (e: Exception) { "" }

                                            val labelStr = if (isRepayment) {
                                                if (isMoneyFlowingToFriend) "Ho rimborsato" else "Ho ricevuto rimborso"
                                            } else {
                                                if (isMoneyFlowingToFriend) "Credito concesso" else "Debito contratto"
                                            }

                                            val isCredito = isMoneyFlowingToFriend
                                            val amountColor = if (isCredito) Color(0xFFF87171) else Color(0xFF34D399) // 🚀 Rosso e Verde sgargianti per dark mode
                                            val amountSign = if (isCredito) "-" else "+"

                                            Row(modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                                Column(modifier = Modifier.weight(1f).padding(end = 8.dp)) {
                                                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                                        Text(
                                                            text = tx.description ?: "Movimento",
                                                            style = MaterialTheme.typography.bodyMedium,
                                                            color = Color.White,
                                                            maxLines = 1,
                                                            modifier = Modifier.weight(1f, fill = false).basicMarquee()
                                                        )
                                                        if (isVirtual) {
                                                            Spacer(Modifier.width(6.dp))
                                                            Surface(color = Color.White.copy(alpha=0.1f), shape = RoundedCornerShape(4.dp)) {
                                                                Text("PROMEMORIA", style = MaterialTheme.typography.labelSmall.copy(fontSize = 8.sp), modifier = Modifier.padding(horizontal = 4.dp, vertical = 2.dp), color = Color.White.copy(alpha=0.8f))
                                                            }
                                                        }
                                                    }
                                                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                                        Text(
                                                            text = labelStr,
                                                            style = MaterialTheme.typography.bodySmall,
                                                            color = Color.White.copy(alpha=0.6f),
                                                            maxLines = 1,
                                                            modifier = Modifier.weight(1f, fill = false).basicMarquee()
                                                        )
                                                        if (txDate.isNotBlank()) {
                                                            Text(" • $txDate", style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.4f), maxLines = 1)
                                                        }
                                                    }
                                                }

                                                ModernMoneyText(
                                                    cents = tx.amountCents,
                                                    sign = amountSign,
                                                    color = amountColor,
                                                    integerFontSize = 16.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp
                                                )

                                                Row(verticalAlignment = Alignment.CenterVertically) {
                                                    if (isVirtual) {
                                                        IconButton(onClick = { txToMakeReal = tx }, modifier = Modifier.size(32.dp)) {
                                                            Icon(Icons.Outlined.CheckCircle, null, modifier = Modifier.size(20.dp), tint = Color.White)
                                                        }
                                                    }
                                                    IconButton(onClick = { transactionToEdit = tx }, modifier = Modifier.size(32.dp)) {
                                                        Icon(Icons.Outlined.Edit, null, modifier = Modifier.size(18.dp), tint = Color.White.copy(alpha=0.7f))
                                                    }
                                                    IconButton(onClick = { transactionToDelete = tx }, modifier = Modifier.size(32.dp)) {
                                                        Icon(Icons.Outlined.Delete, null, modifier = Modifier.size(18.dp), tint = Color(0xFFF87171))
                                                    }
                                                }
                                            }
                                        }

                                        if (!isSettled) {
                                            Spacer(Modifier.height(16.dp))

                                            val activePlan = friendTxs.find { it.categoryId == SYS_DEBT_PLAN_CATEGORY && !it.isDeleted }

                                            if (activePlan != null) {
                                                // --- IL MOTORE MATEMATICO DINAMICO ---
                                                val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
                                                val startLocalDate = try { LocalDate.fromEpochDays(activePlan.dateEpochDay.toInt()) } catch(e:Exception) { today }
                                                val totalMonths = activePlan.amountCents.toInt()

                                                val targetEndDate = startLocalDate.plus(kotlinx.datetime.DatePeriod(months = totalMonths))
                                                val calendarMonthsRemaining = ((targetEndDate.year - today.year) * 12) + (targetEndDate.monthNumber - today.monthNumber)

                                                val baseRemainingMonths = if (today < startLocalDate) totalMonths else calendarMonthsRemaining

                                                val repaidThisMonth = friendTxs.filter { tx ->
                                                    val isVirtual = tx.accountId == SYS_DEBT_REMINDER_ID || tx.fromAccountId == SYS_DEBT_REMINDER_ID || tx.toAccountId == SYS_DEBT_REMINDER_ID
                                                    val isRepayment = tx.subcategoryText?.contains("Rimborso", ignoreCase = true) == true || tx.subcategoryText?.contains("Aggiornamento", ignoreCase = true) == true
                                                    val d = try { LocalDate.fromEpochDays(tx.dateEpochDay.toInt()) } catch(e:Exception) { today }

                                                    isRepayment && !isVirtual && tx.createdAtEpochMs >= activePlan.createdAtEpochMs && d.year == today.year && d.monthNumber == today.monthNumber
                                                }.sumOf { it.amountCents }

                                                val adjustedRemainingMonths = if (repaidThisMonth > 0) (baseRemainingMonths - 1).coerceAtLeast(1) else baseRemainingMonths.coerceAtLeast(1)

                                                val idealInstallment = abs(balance) / adjustedRemainingMonths

                                                val mesi = listOf("Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic")
                                                val endMonthStr = "${mesi[targetEndDate.monthNumber-1]} ${targetEndDate.year}"

                                                Surface(
                                                    modifier = Modifier.fillMaxWidth(),
                                                    shape = RoundedCornerShape(16.dp),
                                                    color = color.copy(alpha = 0.05f),
                                                    border = BorderStroke(1.dp, color.copy(alpha = 0.2f))
                                                ) {
                                                    Column(modifier = Modifier.padding(16.dp)) {
                                                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth()) {
                                                            Row(verticalAlignment = Alignment.CenterVertically) {
                                                                Icon(Icons.Default.CalendarMonth, null, tint = color, modifier = Modifier.size(18.dp))
                                                                Spacer(Modifier.width(8.dp))
                                                                Text("PIANO DI RIENTRO", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = color)
                                                            }
                                                            Row {
                                                                // 🚀 TASTINO MATITA PER MODIFICARE IL PIANO
                                                                IconButton(onClick = { planToEdit = activePlan }, modifier = Modifier.size(28.dp).padding(end = 4.dp)) {
                                                                    Icon(Icons.Outlined.Edit, "Modifica Piano", tint = Color(0xFF60A5FA), modifier = Modifier.size(18.dp))
                                                                }
                                                                IconButton(onClick = { planToCancel = activePlan }, modifier = Modifier.size(28.dp)) {
                                                                    Icon(Icons.Outlined.Delete, "Annulla Piano", tint = MaterialTheme.colorScheme.error, modifier = Modifier.size(18.dp))
                                                                }
                                                            }
                                                        }
                                                        Spacer(Modifier.height(12.dp))
                                                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Bottom) {
                                                            Column {
                                                                Text("Rata ideale", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                                                                Text("$adjustedRemainingMonths rate rimanenti (Fine: $endMonthStr)", style = MaterialTheme.typography.labelSmall.copy(fontSize = 9.sp), color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha=0.7f))
                                                            }
                                                            Column(horizontalAlignment = Alignment.End) {
                                                                ModernMoneyText(cents = idealInstallment, color = color, integerFontSize = 24.sp, decimalFontSize = 14.sp, symbolFontSize = 14.sp)
                                                                if (repaidThisMonth > 0) {
                                                                    Text("GIA' PAGATO: ${formatEuroFromCents(repaidThisMonth, false)}", style = MaterialTheme.typography.labelSmall.copy(fontSize = 9.sp), color = Color(0xFF10B981))
                                                                }
                                                            }
                                                        }
                                                        Spacer(Modifier.height(12.dp))
                                                        Button(
                                                            onClick = {
                                                                suggestedRepayAmount = idealInstallment
                                                                friendToRepay = friend
                                                                isOwedToMeRepay = isOwedToMe
                                                                maxRepayAmount = abs(balance)
                                                            },
                                                            modifier = Modifier.fillMaxWidth().height(40.dp),
                                                            colors = ButtonDefaults.buttonColors(containerColor = color)
                                                        ) {
                                                            Icon(Icons.Outlined.CheckCircle, null, modifier = Modifier.size(16.dp))
                                                            Spacer(Modifier.width(8.dp))
                                                            Text(if (repaidThisMonth > 0) "AGGIUNGI PAGAMENTO" else "PAGA RATA", fontWeight = FontWeight.Bold)
                                                        }
                                                    }
                                                }

                                                // 🚀 I DUE BOTTONI "AUMENTA" E "RIMBORSO" RIPRISTINATI SOTTO IL PIANO
                                                Spacer(Modifier.height(12.dp))
                                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                                    Button(
                                                        onClick = { friendToIncrease = friend; isOwedToMeIncrease = isOwedToMe },
                                                        modifier = Modifier.weight(1f),
                                                        colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha=0.1f), contentColor = Color.White)
                                                    ) {
                                                        Icon(Icons.Default.Add, null, modifier = Modifier.size(16.dp))
                                                        Spacer(Modifier.width(4.dp))
                                                        Text("AUMENTA", fontWeight = FontWeight.Bold, fontSize = 12.sp)
                                                    }
                                                    Button(
                                                        onClick = { suggestedRepayAmount = null; friendToRepay = friend; isOwedToMeRepay = isOwedToMe; maxRepayAmount = abs(balance) },
                                                        modifier = Modifier.weight(1f),
                                                        colors = ButtonDefaults.buttonColors(containerColor = color.copy(alpha=0.15f), contentColor = color)
                                                    ) {
                                                        Icon(Icons.Outlined.Payments, null, modifier = Modifier.size(16.dp))
                                                        Spacer(Modifier.width(4.dp))
                                                        Text("RIMBORSO", fontWeight = FontWeight.Bold, fontSize = 12.sp)
                                                    }
                                                }

                                            } else {
                                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                                    Button(
                                                        onClick = { friendToIncrease = friend; isOwedToMeIncrease = isOwedToMe },
                                                        modifier = Modifier.weight(1f),
                                                        colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha=0.1f), contentColor = Color.White)
                                                    ) {
                                                        Icon(Icons.Default.Add, null, modifier = Modifier.size(16.dp))
                                                        Spacer(Modifier.width(4.dp))
                                                        Text("AUMENTA", fontWeight = FontWeight.Bold, fontSize = 12.sp)
                                                    }
                                                    Button(
                                                        onClick = { suggestedRepayAmount = null; friendToRepay = friend; isOwedToMeRepay = isOwedToMe; maxRepayAmount = abs(balance) },
                                                        modifier = Modifier.weight(1f),
                                                        colors = ButtonDefaults.buttonColors(containerColor = color.copy(alpha=0.15f), contentColor = color)
                                                    ) {
                                                        Icon(Icons.Outlined.CheckCircle, null, modifier = Modifier.size(16.dp))
                                                        Spacer(Modifier.width(4.dp))
                                                        Text("RIMBORSO", fontWeight = FontWeight.Bold, fontSize = 12.sp)
                                                    }
                                                }
                                                OutlinedButton(
                                                    onClick = { friendToCreatePlan = friend },
                                                    modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                                                    shape = RoundedCornerShape(12.dp),
                                                    colors = ButtonDefaults.outlinedButtonColors(contentColor = color),
                                                    border = BorderStroke(1.dp, color.copy(alpha = 0.5f))
                                                ) {
                                                    Icon(Icons.Default.CalendarMonth, null, modifier = Modifier.size(16.dp))
                                                    Spacer(Modifier.width(6.dp))
                                                    Text("CREA PIANO DI RIENTRO", fontWeight = FontWeight.Bold, fontSize = 12.sp)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    } // Chiusura Box Gradient

    // ==========================================
    // 🚀 POPUP GLOBALI DELLA SCHERMATA
    // ==========================================

    transactionToDelete?.let { tx ->
        AlertDialog(
            onDismissRequest = { transactionToDelete = null },
            shape = RoundedCornerShape(24.dp),
            containerColor = MaterialTheme.colorScheme.surface,
            title = { Text("ELIMINA MOVIMENTO", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold)) },
            text = { Text("Vuoi davvero eliminare questo movimento? L'operazione è irreversibile.", style = MaterialTheme.typography.bodyMedium) },
            confirmButton = {
                TextButton(onClick = {
                    onDeleteTransaction(tx)
                    transactionToDelete = null
                    selectedFriendForDetails = null
                }) {
                    Text("ELIMINA", color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { transactionToDelete = null }) { Text("ANNULLA", color = MaterialTheme.colorScheme.onSurfaceVariant) }
            }
        )
    }

    @OptIn(ExperimentalMaterial3Api::class)
    txToMakeReal?.let { tx ->
        var selectedAccountId by remember { mutableStateOf(accounts.firstOrNull { !it.isDeleted && !it.isArchived }?.id ?: "") }
        var accountMenuOpen by remember { mutableStateOf(false) }
        val eligibleAccounts = accounts.filter { !it.isDeleted && !it.isArchived }

        val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
        var dateText by remember { mutableStateOf("${today.dayOfMonth.toString().padStart(2, '0')}/${today.monthNumber.toString().padStart(2, '0')}/${today.year}") }
        var showDatePicker by remember { mutableStateOf(false) }
        val datePickerState = rememberDatePickerState()

        AlertDialog(
            onDismissRequest = { txToMakeReal = null },
            shape = RoundedCornerShape(24.dp),
            containerColor = MaterialTheme.colorScheme.surface,
            title = { Text("Conferma Movimento", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold)) },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                    Text("Scegli la data effettiva e su quale conto registrare questo movimento. Il saldo verrà aggiornato.", style = MaterialTheme.typography.bodyMedium)

                    OutlinedTextField(
                        value = dateText, onValueChange = {}, readOnly = true,
                        label = { Text("Data Effettiva (GG/MM/AAAA)") },
                        trailingIcon = { IconButton(onClick = { showDatePicker = true }) { Icon(Icons.Default.CalendarMonth, null, tint = MaterialTheme.colorScheme.primary) } },
                        modifier = Modifier.fillMaxWidth().onFocusChanged { state -> if(state.isFocused) showDatePicker = true },
                        shape = RoundedCornerShape(12.dp)
                    )

                    ExposedDropdownMenuBox(expanded = accountMenuOpen, onExpandedChange = { accountMenuOpen = !accountMenuOpen }) {
                        val selName = eligibleAccounts.firstOrNull { it.id == selectedAccountId }?.name ?: "SELEZIONA CONTO"
                        OutlinedTextField(
                            value = selName, onValueChange = {}, readOnly = true,
                            label = { Text("Su quale conto?") },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = accountMenuOpen) },
                            modifier = Modifier.menuAnchor().fillMaxWidth(), shape = RoundedCornerShape(12.dp)
                        )
                        ExposedDropdownMenu(expanded = accountMenuOpen, onDismissRequest = { accountMenuOpen = false }) {
                            eligibleAccounts.forEach { a -> DropdownMenuItem(text = { Text(a.name) }, onClick = { selectedAccountId = a.id; accountMenuOpen = false }) }
                        }
                    }
                }
            },
            confirmButton = {
                Button(onClick = {
                    if (selectedAccountId.isNotBlank()) {
                        val isRepayment = tx.subcategoryText?.contains("Rimborso", ignoreCase = true) == true || tx.subcategoryText?.contains("Aggiornamento", ignoreCase = true) == true
                        val newSubcat = if (isRepayment) "Rimborso Reale" else "Gestore Debiti"

                        val parsedDate = try {
                            val parts = dateText.split("/")
                            LocalDate(parts[2].toInt(), parts[1].toInt(), parts[0].toInt()).toEpochDays().toLong()
                        } catch (e: Exception) { today.toEpochDays().toLong() }

                        val updatedTx = tx.copy(
                            accountId = selectedAccountId,
                            fromAccountId = if (tx.fromAccountId == SYS_DEBT_REMINDER_ID) selectedAccountId else tx.fromAccountId,
                            toAccountId = if (tx.toAccountId == SYS_DEBT_REMINDER_ID) selectedAccountId else tx.toAccountId,
                            subcategoryText = newSubcat, dateEpochDay = parsedDate, updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()
                        )
                        onSaveTransaction(updatedTx)
                        txToMakeReal = null
                        selectedFriendForDetails = null
                    }
                }) { Text("CONFERMA", fontWeight = FontWeight.Bold) }
            },
            dismissButton = { TextButton(onClick = { txToMakeReal = null }) { Text("ANNULLA", color = MaterialTheme.colorScheme.onSurfaceVariant) } }
        )

        if (showDatePicker) {
            DatePickerDialog(
                onDismissRequest = { showDatePicker = false },
                confirmButton = {
                    TextButton(onClick = {
                        datePickerState.selectedDateMillis?.let { millis ->
                            val d = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.currentSystemDefault()).date
                            dateText = "${d.dayOfMonth.toString().padStart(2, '0')}/${d.monthNumber.toString().padStart(2, '0')}/${d.year}"
                        }
                        showDatePicker = false
                    }) { Text("OK") }
                }
            ) { DatePicker(state = datePickerState) }
        }
    }

    if (creatingNew) {
        DebtCreationDialog(
            accounts = accounts,
            onDismiss = { creatingNew = false },
            onSave = { friend: String, amountCents: Long, desc: String, accountId: String, iLentTheMoney: Boolean, isReal: Boolean, dateEpochDay: Long ->
                val nowMs = Clock.System.now().toEpochMilliseconds()
                val actualAccount = if (isReal) accountId else SYS_DEBT_REMINDER_ID

                val tx = TransactionEntity(
                    id = "debt_${nowMs}_${Random.nextInt(1000)}", type = "TRANSFER",
                    accountId = actualAccount,
                    fromAccountId = if (iLentTheMoney) actualAccount else SYS_DEBT_ACCOUNT_ID,
                    toAccountId = if (iLentTheMoney) SYS_DEBT_ACCOUNT_ID else actualAccount,
                    amountCents = amountCents, dateEpochDay = dateEpochDay, categoryId = SYS_DEBT_CATEGORY_ID,
                    description = if (desc.isBlank()) "Anticipo" else desc, merchantKey = friend.uppercase().trim(),
                    subcategoryText = if (isReal) "Gestore Debiti" else "Promemoria Debito", logoEmoji = "👥", logoUri = null,
                    isHidden = false, isDeleted = false, reminderDays = null, createdAtEpochMs = nowMs, updatedAtEpochMs = nowMs
                )
                onSaveTransaction(tx)
                creatingNew = false
            }
        )
    }

    // POPUP: RIMBORSI PARZIALI
    if (friendToRepay != null) {
        RepaymentDialog(
            friend = friendToRepay!!,
            maxAmount = maxRepayAmount,
            suggestedAmount = suggestedRepayAmount,
            isOwedToMe = isOwedToMeRepay,
            accounts = accounts,
            onDismiss = { friendToRepay = null },
            onSave = { amountCents: Long, desc: String, isReal: Boolean, accountId: String, dateEpochDay: Long -> // 🚀 ORA RICEVE LA TUA NOTA
                val nowMs = Clock.System.now().toEpochMilliseconds()
                val actualAccount = if (isReal) accountId else SYS_DEBT_REMINDER_ID

                val tx = TransactionEntity(
                    id = "debt_repay_${nowMs}_${Random.nextInt(1000)}", type = "TRANSFER",
                    accountId = actualAccount,
                    fromAccountId = if (isOwedToMeRepay) SYS_DEBT_ACCOUNT_ID else actualAccount,
                    toAccountId = if (isOwedToMeRepay) actualAccount else SYS_DEBT_ACCOUNT_ID,
                    amountCents = amountCents, dateEpochDay = dateEpochDay, categoryId = SYS_DEBT_CATEGORY_ID,
                    // 🚀 SALVA LA TUA NOTA (O un testo predefinito solo se la lasci vuota)
                    description = if (desc.isBlank()) { if (suggestedRepayAmount != null) "Rata Piano di Rientro" else "Rimborso registrato" } else desc,
                    merchantKey = friendToRepay!!.uppercase().trim(),
                    subcategoryText = if (isReal) "Rimborso Reale" else "Aggiornamento Promemoria", logoEmoji = "💸", logoUri = null,
                    isHidden = false, isDeleted = false, reminderDays = null, createdAtEpochMs = nowMs, updatedAtEpochMs = nowMs
                )
                onSaveTransaction(tx)
                friendToRepay = null
                selectedFriendForDetails = null
            }
        )
    }

    // POPUP: AUMENTO PRESTITO
    if (friendToIncrease != null) {
        IncreaseDebtDialog(
            friend = friendToIncrease!!,
            isOwedToMe = isOwedToMeIncrease,
            accounts = accounts,
            onDismiss = { friendToIncrease = null },
            onSave = { amountCents: Long, desc: String, isReal: Boolean, accountId: String, dateEpochDay: Long ->
                val nowMs = Clock.System.now().toEpochMilliseconds()
                val actualAccount = if (isReal) accountId else SYS_DEBT_REMINDER_ID

                val tx = TransactionEntity(
                    id = "debt_inc_${nowMs}_${Random.nextInt(1000)}", type = "TRANSFER",
                    accountId = actualAccount,
                    fromAccountId = if (isOwedToMeIncrease) actualAccount else SYS_DEBT_ACCOUNT_ID,
                    toAccountId = if (isOwedToMeIncrease) SYS_DEBT_ACCOUNT_ID else actualAccount,
                    amountCents = amountCents, dateEpochDay = dateEpochDay, categoryId = SYS_DEBT_CATEGORY_ID,
                    description = if (desc.isBlank()) "Nuovo prestito/lavoro" else desc, merchantKey = friendToIncrease!!.uppercase().trim(),
                    subcategoryText = if (isReal) "Gestore Debiti" else "Promemoria Debito", logoEmoji = "👥", logoUri = null,
                    isHidden = false, isDeleted = false, reminderDays = null, createdAtEpochMs = nowMs, updatedAtEpochMs = nowMs
                )
                onSaveTransaction(tx)
                friendToIncrease = null
                selectedFriendForDetails = null
            }
        )
    }

    // POPUP: MODIFICA MOVIMENTO ESISTENTE
    transactionToEdit?.let { tx ->
        EditDebtDialog(
            originalTx = tx,
            onDismiss = { transactionToEdit = null },
            onSave = { updatedTx: TransactionEntity ->
                onSaveTransaction(updatedTx)
                transactionToEdit = null
            }
        )
    }

    // POPUP: MODIFICA GLOBALE ANAGRAFICA
    friendToEditGlobal?.let { oldName ->
        val friendTxs = debtTransactions.filter { it.merchantKey?.uppercase() == oldName }

        GlobalEditDebtDialog(
            oldName = oldName,
            onDismiss = { friendToEditGlobal = null },
            onSave = { newName: String ->
                // 🚀 IL CICLO SICURO: Aggiorna SOLO il nome, lasciando intatte le tue note!
                friendTxs.forEach { tx ->
                    val updatedTx = tx.copy(
                        merchantKey = newName.uppercase().trim(),
                        // RIMOSSA LA SOVRASCRITTURA DELLE NOTE!
                        updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()
                    )
                    onSaveTransaction(updatedTx)
                }
                friendToEditGlobal = null
                selectedFriendForDetails = null
            }
        )
    }

    // POPUP: ELIMINAZIONE GLOBALE
    friendToDeleteGlobal?.let { friendName ->
        AlertDialog(
            onDismissRequest = { friendToDeleteGlobal = null },
            shape = RoundedCornerShape(24.dp),
            containerColor = MaterialTheme.colorScheme.surface,
            title = { Text("ELIMINA INTERO DEBITO", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold)) },
            text = { Text("Vuoi davvero eliminare l'intero storico di '$friendName'?\nL'operazione cancellerà tutti i prestiti e i rimborsi collegati in un colpo solo ed è irreversibile.", style = MaterialTheme.typography.bodyMedium) },
            confirmButton = {
                TextButton(onClick = {
                    val friendTxs = debtTransactions.filter { it.merchantKey?.uppercase() == friendName }
                    friendTxs.forEach { tx -> onDeleteTransaction(tx) }
                    friendToDeleteGlobal = null
                    selectedFriendForDetails = null
                }) { Text("ELIMINA TUTTO", color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold) }
            },
            dismissButton = { TextButton(onClick = { friendToDeleteGlobal = null }) { Text("ANNULLA", color = MaterialTheme.colorScheme.onSurfaceVariant) } }
        )
    }

    // 🚀 GESTIONE UNIFICATA PER CREARE O MODIFICARE UN PIANO DI RIENTRO
    if (friendToCreatePlan != null || planToEdit != null) {
        val isEditing = planToEdit != null
        val targetFriend = friendToCreatePlan ?: planToEdit?.merchantKey ?: ""

        PlanSetupDialog(
            friendName = targetFriend,
            initialMonths = planToEdit?.amountCents?.toString() ?: "6",
            initialDateEpoch = planToEdit?.dateEpochDay,
            initialDescription = planToEdit?.description, // 🚀 PASSIAMO LA TUA VECCHIA NOTA PER NON PERDERLA
            onDismiss = { friendToCreatePlan = null; planToEdit = null },
            onSave = { months: Long, startEpoch: Long, desc: String -> // 🚀 ORA RICEVE LA TUA NOTA
                if (isEditing) {
                    val updatedTx = planToEdit!!.copy(
                        amountCents = months,
                        dateEpochDay = startEpoch,
                        description = if (desc.isBlank()) "Piano di Rientro" else desc, // 🚀 AGGIORNA CON LE TUE PAROLE
                        updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()
                    )
                    onSaveTransaction(updatedTx)
                } else {
                    val nowMs = Clock.System.now().toEpochMilliseconds()
                    val planTx = TransactionEntity(
                        id = "sys_plan_${nowMs}_${kotlin.random.Random.nextInt(1000)}",
                        accountId = SYS_DEBT_REMINDER_ID, toAccountId = null, fromAccountId = null,
                        amountCents = months, type = "TRANSFER",
                        dateEpochDay = startEpoch,
                        categoryId = SYS_DEBT_PLAN_CATEGORY,
                        description = if (desc.isBlank()) "Piano di Rientro Attivo" else desc, // 🚀 SALVA LA TUA NOTA
                        merchantKey = targetFriend.uppercase().trim(),
                        subcategoryText = "Setup Piano", logoEmoji = "📅", logoUri = null,
                        isHidden = false, isDeleted = false, reminderDays = null,
                        createdAtEpochMs = nowMs, updatedAtEpochMs = nowMs
                    )
                    onSaveTransaction(planTx)
                }
                friendToCreatePlan = null
                planToEdit = null
            }
        )
    }

    // POPUP: ANNULLA PIANO DI RIENTRO
    planToCancel?.let { tx ->
        AlertDialog(
            onDismissRequest = { planToCancel = null },
            shape = RoundedCornerShape(24.dp),
            title = { Text("Annulla Piano", color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold) },
            text = { Text("Vuoi annullare il piano di rientro? Il debito totale e i rimborsi già pagati rimarranno intatti, ma perderai il calcolo dinamico della rata.") },
            confirmButton = {
                TextButton(onClick = {
                    onDeleteTransaction(tx)
                    planToCancel = null
                    selectedFriendForDetails = null
                }) { Text("ANNULLA PIANO", color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold) }
            },
            dismissButton = { TextButton(onClick = { planToCancel = null }) { Text("INDIETRO") } }
        )
    }
} // Chiusura Scaffold
} // 🚀 Chiusura DEFINITIVA di DebtManagerScreen

// ==========================================
// 🚀 COMPONENTI GRAFICI ESTERNI
// ==========================================

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PlanSetupDialog(
    friendName: String,
    initialMonths: String,
    initialDateEpoch: Long?,
    initialDescription: String?, // 🚀 NUOVO CAMPO
    onDismiss: () -> Unit,
    onSave: (months: Long, startEpoch: Long, desc: String) -> Unit // 🚀 NUOVO CAMPO
) {
    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())

    var startDate by remember { mutableStateOf(initialDateEpoch?.let { try{ LocalDate.fromEpochDays(it.toInt()) }catch(e:Exception){today} } ?: today) }
    val initialM = initialMonths.toIntOrNull() ?: 6
    var endDate by remember { mutableStateOf(startDate.plus(kotlinx.datetime.DatePeriod(months = initialM))) }

    var description by remember { mutableStateOf(initialDescription ?: "") } // 🚀 MEMORIZZA LA TUA NOTA

    var showStartDatePicker by remember { mutableStateOf(false) }
    val startDatePickerState = rememberDatePickerState()

    var showEndDatePicker by remember { mutableStateOf(false) }
    val endDatePickerState = rememberDatePickerState()

    // 🚀 La stima temporale di base: la differenza tra i mesi (+1 per comprendere l'inizio e la fine)
    val timeSpanMonths = maxOf(1, ((endDate.year - startDate.year) * 12) + (endDate.monthNumber - startDate.monthNumber) + 1)
    
    // 🚀 Il TUO stato modificabile, pre-riempito con la stima, oppure col vecchio dato se sto modificando
    var customInstallmentsText by remember { mutableStateOf(if (initialDateEpoch != null) initialMonths else timeSpanMonths.toString()) }
    
    // Se cambi le date, aggiorna in modo smart il numero di rate, MA SOLO se non l'hai bloccato/toccato manualmente a qualcosa di strano
    LaunchedEffect(startDate, endDate) {
        if (initialDateEpoch == null) {
            val newSpan = maxOf(1, ((endDate.year - startDate.year) * 12) + (endDate.monthNumber - startDate.monthNumber) + 1)
            customInstallmentsText = newSpan.toString()
        }
    }

    val finalInstallments = customInstallmentsText.toIntOrNull() ?: 0

    val mesi = listOf("Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic")
    val startStr = "${startDate.dayOfMonth.toString().padStart(2, '0')} ${mesi[startDate.monthNumber-1]} ${startDate.year}"
    val endStr = "${endDate.dayOfMonth.toString().padStart(2, '0')} ${mesi[endDate.monthNumber-1]} ${endDate.year}"

    AlertDialog(
        onDismissRequest = onDismiss,
        shape = RoundedCornerShape(28.dp),
        containerColor = Color.White.copy(alpha = 0.12f),
        title = { Text(if (initialDateEpoch == null) "Nuovo Piano di Rientro" else "Modifica Piano", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color(0xFF60A5FA)) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                Text("Seleziona la data di inizio, di fine, e in quante rate vuoi spezzare il debito con $friendName.", style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0))

                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(
                        value = startStr, onValueChange = {}, readOnly = true,
                        label = { Text("Inizio", fontSize = 12.sp, color = Color(0xFFA0A0A0)) },
                        trailingIcon = { IconButton(onClick = { showStartDatePicker = true }) { Icon(Icons.Default.CalendarMonth, null, tint = Color.White, modifier = Modifier.size(20.dp)) } },
                        modifier = Modifier.weight(1f).onFocusChanged { state -> if(state.isFocused) showStartDatePicker = true },
                        shape = RoundedCornerShape(12.dp),
                        colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                    )

                    OutlinedTextField(
                        value = endStr, onValueChange = {}, readOnly = true,
                        label = { Text("Fine", fontSize = 12.sp, color = Color(0xFFA0A0A0)) },
                        trailingIcon = { IconButton(onClick = { showEndDatePicker = true }) { Icon(Icons.Default.CalendarMonth, null, tint = Color.White, modifier = Modifier.size(20.dp)) } },
                        modifier = Modifier.weight(1f).onFocusChanged { state -> if(state.isFocused) showEndDatePicker = true },
                        shape = RoundedCornerShape(12.dp),
                        colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                    )
                }

                // 🚀 IL NUOVO CAMPO PER IL NUMERO DI RATE
                OutlinedTextField(
                    value = customInstallmentsText,
                    onValueChange = { newVal ->
                        if (newVal.all { it.isDigit() }) customInstallmentsText = newVal
                    },
                    label = { Text("Numero di Rate", fontSize = 12.sp, color = Color(0xFFA0A0A0)) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                // 🚀 IL NUOVO CAMPO PER LE TUE NOTE
                OutlinedTextField(
                    value = description, onValueChange = { description = it },
                    label = { Text("Motivo / Causale (Opzionale)", fontSize = 12.sp, color = Color(0xFFA0A0A0)) },
                    placeholder = { Text("Es. Piano di Rientro", color = Color(0xFFA0A0A0).copy(alpha = 0.5f)) },
                    modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                Surface(color = Color.White.copy(alpha = 0.08f), shape = RoundedCornerShape(12.dp), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))) {
                    Row(Modifier.fillMaxWidth().padding(16.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                        Text("Durata del piano:", color = Color(0xFFA0A0A0), fontSize = 14.sp)
                        Text(text = if (finalInstallments > 0) "$finalInstallments Rate" else "Inserisci rate", fontWeight = FontWeight.Black, color = if (finalInstallments > 0) Color(0xFF60A5FA) else MaterialTheme.colorScheme.error)
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { if (finalInstallments > 0) onSave(finalInstallments.toLong(), startDate.toEpochDays().toLong(), description.trim()) },
                enabled = finalInstallments > 0,
                shape = RoundedCornerShape(14.dp),
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White)
            ) { Text(if (initialDateEpoch == null) "CREA PIANO" else "SALVA PIANO", fontWeight = FontWeight.Bold) }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) } }
    )

    if (showStartDatePicker) {
        DatePickerDialog(
            onDismissRequest = { showStartDatePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    startDatePickerState.selectedDateMillis?.let { millis ->
                        startDate = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.currentSystemDefault()).date
                        if (startDate >= endDate) endDate = startDate.plus(kotlinx.datetime.DatePeriod(months = 1))
                    }
                    showStartDatePicker = false
                }) { Text("OK", fontWeight = FontWeight.Bold, color = Color(0xFF60A5FA)) }
            },
            dismissButton = {
                TextButton(onClick = { showStartDatePicker = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            },
            colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B))
        ) { DatePicker(state = startDatePickerState, colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B), titleContentColor = Color.White, headlineContentColor = Color.White, weekdayContentColor = Color(0xFFA0A0A0), dayContentColor = Color.White, todayContentColor = Color(0xFF60A5FA))) }
    }

    if (showEndDatePicker) {
        DatePickerDialog(
            onDismissRequest = { showEndDatePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    endDatePickerState.selectedDateMillis?.let { millis ->
                        endDate = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.currentSystemDefault()).date
                    }
                    showEndDatePicker = false
                }) { Text("OK", fontWeight = FontWeight.Bold, color = Color(0xFF60A5FA)) }
            },
            dismissButton = {
                TextButton(onClick = { showEndDatePicker = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            },
            colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B))
        ) { DatePicker(state = endDatePickerState, colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B), titleContentColor = Color.White, headlineContentColor = Color.White, weekdayContentColor = Color(0xFFA0A0A0), dayContentColor = Color.White, todayContentColor = Color(0xFF60A5FA))) }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DebtCreationDialog(
    accounts: List<AccountEntity>,
    onDismiss: () -> Unit,
    onSave: (friend: String, amountCents: Long, desc: String, accountId: String, iLentTheMoney: Boolean, isRealTransaction: Boolean, dateEpochDay: Long) -> Unit
) {
    var friendName by remember { mutableStateOf("") }
    var amountText by remember { mutableStateOf("") }
    var hasCleared by remember { mutableStateOf(false) }

    var description by remember { mutableStateOf("") }
    var iLentTheMoney by remember { mutableStateOf(true) }
    var isRealTransaction by remember { mutableStateOf(false) }

    val eligibleAccounts = accounts.filter { !it.isArchived && !it.isDeleted }
    var selectedAccountId by remember { mutableStateOf(eligibleAccounts.firstOrNull()?.id ?: "") }
    var accountMenuOpen by remember { mutableStateOf(false) }

    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
    var dateText by remember { mutableStateOf("${today.dayOfMonth.toString().padStart(2, '0')}/${today.monthNumber.toString().padStart(2, '0')}/${today.year}") }
    var showDatePicker by remember { mutableStateOf(false) }
    val datePickerState = rememberDatePickerState()

    Dialog(onDismissRequest = onDismiss, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Surface(
            modifier = Modifier.padding(16.dp).fillMaxWidth().widthIn(max = 400.dp).imePadding(),
            shape = RoundedCornerShape(28.dp),
            color = Color.White.copy(alpha = 0.12f),
            border = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.40f)),
            shadowElevation = 24.dp
        ) {
            Column(modifier = Modifier.padding(24.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                Text("Registra Debito / Credito", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold), color = Color(0xFF60A5FA))

                Row(modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(12.dp)).background(Color.White.copy(alpha = 0.10f))) {
                    Box(modifier = Modifier.weight(1f).clickable { iLentTheMoney = true }.background(if (iLentTheMoney) Color(0xFF60A5FA) else Color.Transparent).padding(vertical = 12.dp), contentAlignment = Alignment.Center) {
                        Text("IO HO DATO", color = if (iLentTheMoney) Color.White else Color(0xFFA0A0A0), fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                    }
                    Box(modifier = Modifier.weight(1f).clickable { iLentTheMoney = false }.background(if (!iLentTheMoney) MaterialTheme.colorScheme.error else Color.Transparent).padding(vertical = 12.dp), contentAlignment = Alignment.Center) {
                        Text("IO HO RICEVUTO", color = if (!iLentTheMoney) Color.White else Color(0xFFA0A0A0), fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                    }
                }

                OutlinedTextField(
                    value = friendName, onValueChange = { friendName = it },
                    label = { Text("Nome Persona/Ente", color = Color(0xFFA0A0A0)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                OutlinedTextField(
                    value = amountText, onValueChange = { amountText = it.replace(Regex("[^0-9.,]"), "") },
                    label = { Text("Importo (€)", color = Color(0xFFA0A0A0)) }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                    modifier = Modifier.fillMaxWidth().onFocusChanged { state ->
                        if (state.isFocused && !hasCleared) {
                            amountText = ""
                            hasCleared = true
                        }
                    },
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                OutlinedTextField(
                    value = dateText,
                    onValueChange = {},
                    readOnly = true,
                    label = { Text("Data (GG/MM/AAAA)", color = Color(0xFFA0A0A0)) },
                    trailingIcon = {
                        IconButton(onClick = { showDatePicker = true }) {
                            Icon(Icons.Default.CalendarMonth, null, tint = Color.White)
                        }
                    },
                    modifier = Modifier.fillMaxWidth().onFocusChanged { state -> if(state.isFocused) showDatePicker = true },
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                OutlinedTextField(
                    value = description, onValueChange = { description = it },
                    label = { Text("Motivo (es. Dentista, Cena)", color = Color(0xFFA0A0A0)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    color = Color.White.copy(alpha = 0.08f),
                    border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))
                ) {
                    Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                        MonetixGlassSwitch(checked = isRealTransaction, onCheckedChange = { isRealTransaction = it }, activeColor = Color(0xFF60A5FA))
                        Spacer(Modifier.width(12.dp))
                        Column(modifier = Modifier.weight(1f)) {
                            Text("Movimenta il conto", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = Color.White)
                            Text(
                                text = if (isRealTransaction) (if(iLentTheMoney) "I soldi escono dal tuo conto" else "I soldi entrano nel tuo conto") else "Solo promemoria (Saldi intatti)",
                                style = MaterialTheme.typography.bodySmall,
                                color = Color(0xFFA0A0A0),
                                maxLines = 1,
                                modifier = Modifier.basicMarquee()
                            )
                        }
                    }
                }

                AnimatedVisibility(visible = isRealTransaction) {
                    ExposedDropdownMenuBox(expanded = accountMenuOpen, onExpandedChange = { accountMenuOpen = !accountMenuOpen }) {
                        val selName = eligibleAccounts.firstOrNull { it.id == selectedAccountId }?.name ?: "SELEZIONA CONTO"
                        OutlinedTextField(
                            value = selName, onValueChange = {}, readOnly = true,
                            label = { Text(if (iLentTheMoney) "Usciti da quale conto?" else "Ricevuti su quale conto?", color = Color(0xFFA0A0A0)) },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = accountMenuOpen) },
                            modifier = Modifier.menuAnchor().fillMaxWidth(), shape = RoundedCornerShape(12.dp),
                            colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                        )
                        ExposedDropdownMenu(expanded = accountMenuOpen, onDismissRequest = { accountMenuOpen = false }, modifier = Modifier.background(Color(0xFF1C1C1E))) {
                            eligibleAccounts.forEach { a -> DropdownMenuItem(text = { Text(a.name, color = Color.White) }, onClick = { selectedAccountId = a.id; accountMenuOpen = false }) }
                        }
                    }
                }

                Row(modifier = Modifier.fillMaxWidth().padding(top = 8.dp), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
                    Spacer(Modifier.width(12.dp))
                    Button(
                        onClick = {
                            val cents = (amountText.replace(",", ".").toDoubleOrNull()?.times(100))?.toLong() ?: 0L

                            val txEpochDay = try {
                                val parts = dateText.split("/")
                                LocalDate(parts[2].toInt(), parts[1].toInt(), parts[0].toInt()).toEpochDays().toLong()
                            } catch (e: Exception) {
                                today.toEpochDays().toLong()
                            }

                            if (friendName.isNotBlank() && cents > 0L && (!isRealTransaction || selectedAccountId.isNotBlank())) {
                                onSave(friendName, cents, description, selectedAccountId, iLentTheMoney, isRealTransaction, txEpochDay)
                            }
                        },
                        shape = RoundedCornerShape(14.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White)
                    ) { Text("SALVA", fontWeight = FontWeight.Bold) }
                }
            }
        }
    }

    if (showDatePicker) {
        DatePickerDialog(
            onDismissRequest = { showDatePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    datePickerState.selectedDateMillis?.let { millis ->
                        val d = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.currentSystemDefault()).date
                        dateText = "${d.dayOfMonth.toString().padStart(2, '0')}/${d.monthNumber.toString().padStart(2, '0')}/${d.year}"
                    }
                    showDatePicker = false
                }) { Text("OK", fontWeight = FontWeight.Bold, color = Color(0xFF60A5FA)) }
            },
            dismissButton = {
                TextButton(onClick = { showDatePicker = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            },
            colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B))
        ) { DatePicker(state = datePickerState, colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B), titleContentColor = Color.White, headlineContentColor = Color.White, weekdayContentColor = Color(0xFFA0A0A0), dayContentColor = Color.White, todayContentColor = Color(0xFF60A5FA))) }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RepaymentDialog(
    friend: String,
    maxAmount: Long,
    suggestedAmount: Long?,
    isOwedToMe: Boolean,
    accounts: List<AccountEntity>,
    onDismiss: () -> Unit,
    onSave: (amountCents: Long, desc: String, isRealTransaction: Boolean, accountId: String, dateEpochDay: Long) -> Unit // 🚀 ORA SPEDISCE LA TUA NOTA
) {
    var amountText by remember { mutableStateOf(
        if (suggestedAmount != null) {
            if (suggestedAmount % 100 == 0L) (suggestedAmount / 100).toString()
            else "${suggestedAmount / 100},${(suggestedAmount % 100).toString().padStart(2, '0')}"
        } else {
            formatEuroFromCents(maxAmount, false).replace(" €", "")
        }
    ) }
    var hasCleared by remember { mutableStateOf(false) }
    var isRealTransaction by remember { mutableStateOf(false) }
    var description by remember { mutableStateOf("") } // 🚀 STATO PER LE TUE NOTE

    val eligibleAccounts = accounts.filter { !it.isArchived && !it.isDeleted }
    var selectedAccountId by remember { mutableStateOf(eligibleAccounts.firstOrNull()?.id ?: "") }
    var accountMenuOpen by remember { mutableStateOf(false) }

    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
    var dateText by remember { mutableStateOf("${today.dayOfMonth.toString().padStart(2, '0')}/${today.monthNumber.toString().padStart(2, '0')}/${today.year}") }
    var showDatePicker by remember { mutableStateOf(false) }
    val datePickerState = rememberDatePickerState()

    Dialog(onDismissRequest = onDismiss, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Surface(
            modifier = Modifier.padding(16.dp).fillMaxWidth().widthIn(max = 400.dp).imePadding(),
            shape = RoundedCornerShape(28.dp),
            color = Color.White.copy(alpha = 0.12f),
            border = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.40f)),
            shadowElevation = 24.dp
        ) {
            Column(modifier = Modifier.padding(24.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                Text(if (isOwedToMe) "Ricevi Rimborso" else "Paga Debito", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold), color = if(isOwedToMe) Color(0xFF10B981) else MaterialTheme.colorScheme.error)
                Text(if (isOwedToMe) "Quanto ti sta restituendo $friend?" else "Quanto stai restituendo a $friend?", style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0))

                if (suggestedAmount != null) {
                    Text("Puoi regolare l'importo qui sotto. Se paghi di più o di meno, il piano ricalcolerà automaticamente le rate future!", style = MaterialTheme.typography.labelSmall, color = Color(0xFF60A5FA))
                }

                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    IconButton(onClick = {
                        val currentCents = (amountText.replace(",", ".").toDoubleOrNull()?.times(100))?.toLong() ?: 0L
                        val newCents = (currentCents - 1000L).coerceAtLeast(0L)
                        amountText = if (newCents % 100 == 0L) (newCents / 100).toString() else "${newCents / 100},${(newCents % 100).toString().padStart(2, '0')}"
                    }, modifier = Modifier.background(Color.White.copy(alpha = 0.10f), CircleShape)) {
                        Icon(Icons.Default.Remove, null, tint = Color.White)
                    }

                    OutlinedTextField(
                        value = amountText, onValueChange = { amountText = it.replace(Regex("[^0-9.,]"), "") },
                        label = { Text("Importo (€)", color = Color(0xFFA0A0A0)) }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                        modifier = Modifier.weight(1f).onFocusChanged { state ->
                            if (state.isFocused && !hasCleared) {
                                amountText = ""
                                hasCleared = true
                            }
                        },
                        shape = RoundedCornerShape(12.dp), singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                    )

                    IconButton(onClick = {
                        val currentCents = (amountText.replace(",", ".").toDoubleOrNull()?.times(100))?.toLong() ?: 0L
                        val newCents = currentCents + 1000L
                        amountText = if (newCents % 100 == 0L) (newCents / 100).toString() else "${newCents / 100},${(newCents % 100).toString().padStart(2, '0')}"
                    }, modifier = Modifier.background(Color.White.copy(alpha = 0.10f), CircleShape)) {
                        Icon(Icons.Default.Add, null, tint = Color.White)
                    }
                }

                // 🚀 IL NUOVO CAMPO PER LE NOTE SUI PAGAMENTI
                OutlinedTextField(
                    value = description, onValueChange = { description = it },
                    label = { Text("Causale / Note (Opzionale)", color = Color(0xFFA0A0A0)) },
                    placeholder = { Text(if (suggestedAmount != null) "Es. Rata piano di rientro" else "Es. Restituzione soldi cena", color = Color(0xFFA0A0A0).copy(alpha = 0.5f)) },
                    modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                OutlinedTextField(
                    value = dateText,
                    onValueChange = {},
                    readOnly = true,
                    label = { Text("Data (GG/MM/AAAA)", color = Color(0xFFA0A0A0)) },
                    trailingIcon = {
                        IconButton(onClick = { showDatePicker = true }) {
                            Icon(Icons.Default.CalendarMonth, null, tint = Color.White)
                        }
                    },
                    modifier = Modifier.fillMaxWidth().onFocusChanged { state -> if(state.isFocused) showDatePicker = true },
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    color = Color.White.copy(alpha = 0.08f),
                    border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))
                ) {
                    Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                        MonetixGlassSwitch(checked = isRealTransaction, onCheckedChange = { isRealTransaction = it }, activeColor = Color(0xFF60A5FA))
                        Spacer(Modifier.width(12.dp))
                        Column(modifier = Modifier.weight(1f)) {
                            Text("Movimenta il conto", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = Color.White)
                            Text(
                                text = if (isRealTransaction) (if(isOwedToMe) "Aggiunge i soldi al saldo reale" else "Scala i soldi dal saldo reale") else "Aggiorna solo il promemoria",
                                style = MaterialTheme.typography.bodySmall,
                                color = Color(0xFFA0A0A0),
                                maxLines = 1,
                                modifier = Modifier.basicMarquee()
                            )
                        }
                    }
                }

                AnimatedVisibility(visible = isRealTransaction) {
                    ExposedDropdownMenuBox(expanded = accountMenuOpen, onExpandedChange = { accountMenuOpen = !accountMenuOpen }) {
                        val selName = eligibleAccounts.firstOrNull { it.id == selectedAccountId }?.name ?: "SELEZIONA CONTO"
                        OutlinedTextField(
                            value = selName, onValueChange = {}, readOnly = true,
                            label = { Text(if (isOwedToMe) "Su quale conto?" else "Da quale conto?", color = Color(0xFFA0A0A0)) },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = accountMenuOpen) },
                            modifier = Modifier.menuAnchor().fillMaxWidth(), shape = RoundedCornerShape(12.dp),
                            colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                        )
                        ExposedDropdownMenu(expanded = accountMenuOpen, onDismissRequest = { accountMenuOpen = false }, modifier = Modifier.background(Color(0xFF1C1C1E))) {
                            eligibleAccounts.forEach { a -> DropdownMenuItem(text = { Text(a.name, color = Color.White) }, onClick = { selectedAccountId = a.id; accountMenuOpen = false }) }
                        }
                    }
                }

                Row(modifier = Modifier.fillMaxWidth().padding(top = 8.dp), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
                    Spacer(Modifier.width(12.dp))
                    Button(
                        onClick = {
                            val cents = (amountText.replace(",", ".").toDoubleOrNull()?.times(100))?.toLong() ?: 0L

                            val txEpochDay = try {
                                val parts = dateText.split("/")
                                LocalDate(parts[2].toInt(), parts[1].toInt(), parts[0].toInt()).toEpochDays().toLong()
                            } catch (e: Exception) {
                                today.toEpochDays().toLong()
                            }

                            if (cents > 0L && (!isRealTransaction || selectedAccountId.isNotBlank())) {
                                onSave(cents, description.trim(), isRealTransaction, selectedAccountId, txEpochDay)
                            }
                        },
                        shape = RoundedCornerShape(14.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = if (isOwedToMe) Color(0xFF10B981) else MaterialTheme.colorScheme.error, contentColor = Color.White)
                    ) { Text("REGISTRA", fontWeight = FontWeight.Bold) }
                }
            }
        }
    }

    if (showDatePicker) {
        DatePickerDialog(
            onDismissRequest = { showDatePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    datePickerState.selectedDateMillis?.let { millis ->
                        val d = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.currentSystemDefault()).date
                        dateText = "${d.dayOfMonth.toString().padStart(2, '0')}/${d.monthNumber.toString().padStart(2, '0')}/${d.year}"
                    }
                    showDatePicker = false
                }) { Text("OK", fontWeight = FontWeight.Bold, color = Color(0xFF60A5FA)) }
            },
            dismissButton = {
                TextButton(onClick = { showDatePicker = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            },
            colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B))
        ) { DatePicker(state = datePickerState, colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B), titleContentColor = Color.White, headlineContentColor = Color.White, weekdayContentColor = Color(0xFFA0A0A0), dayContentColor = Color.White, todayContentColor = Color(0xFF60A5FA))) }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun IncreaseDebtDialog(
    friend: String,
    isOwedToMe: Boolean,
    accounts: List<AccountEntity>,
    onDismiss: () -> Unit,
    onSave: (amountCents: Long, desc: String, isRealTransaction: Boolean, accountId: String, dateEpochDay: Long) -> Unit
) {
    var amountText by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var isRealTransaction by remember { mutableStateOf(false) }
    var hasCleared by remember { mutableStateOf(false) }

    val eligibleAccounts = accounts.filter { !it.isArchived && !it.isDeleted }
    var selectedAccountId by remember { mutableStateOf(eligibleAccounts.firstOrNull()?.id ?: "") }
    var accountMenuOpen by remember { mutableStateOf(false) }

    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
    var dateText by remember { mutableStateOf("${today.dayOfMonth.toString().padStart(2, '0')}/${today.monthNumber.toString().padStart(2, '0')}/${today.year}") }
    var showDatePicker by remember { mutableStateOf(false) }
    val datePickerState = rememberDatePickerState()

    Dialog(onDismissRequest = onDismiss, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Surface(
            modifier = Modifier.padding(16.dp).fillMaxWidth().widthIn(max = 400.dp).imePadding(),
            shape = RoundedCornerShape(28.dp),
            color = Color.White.copy(alpha = 0.12f),
            border = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.40f)),
            shadowElevation = 24.dp
        ) {
            Column(modifier = Modifier.padding(24.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                Text(if (isOwedToMe) "Aumenta Credito" else "Aumenta Debito", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold), color = Color(0xFF60A5FA))
                Text(if (isOwedToMe) "Hai prestato altri soldi o fatto un altro lavoro per $friend?" else "Ti sei fatto prestare altri soldi da $friend?", style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0))

                OutlinedTextField(
                    value = amountText, onValueChange = { amountText = it.replace(Regex("[^0-9.,]"), "") },
                    label = { Text("Nuovo Importo (€)", color = Color(0xFFA0A0A0)) }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                    modifier = Modifier.fillMaxWidth().onFocusChanged { state ->
                        if (state.isFocused && !hasCleared) {
                            amountText = ""
                            hasCleared = true
                        }
                    },
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                OutlinedTextField(
                    value = description, onValueChange = { description = it },
                    label = { Text("Motivo (es. Secondo lavoro)", color = Color(0xFFA0A0A0)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                OutlinedTextField(
                    value = dateText,
                    onValueChange = {},
                    readOnly = true,
                    label = { Text("Data (GG/MM/AAAA)", color = Color(0xFFA0A0A0)) },
                    trailingIcon = {
                        IconButton(onClick = { showDatePicker = true }) {
                            Icon(Icons.Default.CalendarMonth, null, tint = Color.White)
                        }
                    },
                    modifier = Modifier.fillMaxWidth().onFocusChanged { state -> if(state.isFocused) showDatePicker = true },
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    color = Color.White.copy(alpha = 0.08f),
                    border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))
                ) {
                    Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                        MonetixGlassSwitch(checked = isRealTransaction, onCheckedChange = { isRealTransaction = it }, activeColor = Color(0xFF60A5FA))
                        Spacer(Modifier.width(12.dp))
                        Column(modifier = Modifier.weight(1f)) {
                            Text("Movimenta il conto", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = Color.White)
                            Text(
                                text = if (isRealTransaction) (if(isOwedToMe) "I soldi escono dal tuo saldo reale" else "I soldi entrano nel tuo saldo reale") else "Aggiorna solo il promemoria",
                                style = MaterialTheme.typography.bodySmall,
                                color = Color(0xFFA0A0A0),
                                maxLines = 1,
                                modifier = Modifier.basicMarquee()
                            )
                        }
                    }
                }

                AnimatedVisibility(visible = isRealTransaction) {
                    ExposedDropdownMenuBox(expanded = accountMenuOpen, onExpandedChange = { accountMenuOpen = !accountMenuOpen }) {
                        val selName = eligibleAccounts.firstOrNull { it.id == selectedAccountId }?.name ?: "SELEZIONA CONTO"
                        OutlinedTextField(
                            value = selName, onValueChange = {}, readOnly = true,
                            label = { Text(if (isOwedToMe) "Usciti da quale conto?" else "Ricevuti su quale conto?", color = Color(0xFFA0A0A0)) },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = accountMenuOpen) },
                            modifier = Modifier.menuAnchor().fillMaxWidth(), shape = RoundedCornerShape(12.dp),
                            colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                        )
                        ExposedDropdownMenu(expanded = accountMenuOpen, onDismissRequest = { accountMenuOpen = false }, modifier = Modifier.background(Color(0xFF1C1C1E))) {
                            eligibleAccounts.forEach { a -> DropdownMenuItem(text = { Text(a.name, color = Color.White) }, onClick = { selectedAccountId = a.id; accountMenuOpen = false }) }
                        }
                    }
                }

                Row(modifier = Modifier.fillMaxWidth().padding(top = 8.dp), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
                    Spacer(Modifier.width(12.dp))
                    Button(
                        onClick = {
                            val cents = (amountText.replace(",", ".").toDoubleOrNull()?.times(100))?.toLong() ?: 0L

                            val txEpochDay = try {
                                val parts = dateText.split("/")
                                LocalDate(parts[2].toInt(), parts[1].toInt(), parts[0].toInt()).toEpochDays().toLong()
                            } catch (e: Exception) {
                                today.toEpochDays().toLong()
                            }

                            if (cents > 0L && (!isRealTransaction || selectedAccountId.isNotBlank())) {
                                onSave(cents, description, isRealTransaction, selectedAccountId, txEpochDay)
                            }
                        },
                        shape = RoundedCornerShape(14.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White)
                    ) { Text("AGGIUNGI", fontWeight = FontWeight.Bold) }
                }
            }
        }
    }

    if (showDatePicker) {
        DatePickerDialog(
            onDismissRequest = { showDatePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    datePickerState.selectedDateMillis?.let { millis ->
                        val d = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.currentSystemDefault()).date
                        dateText = "${d.dayOfMonth.toString().padStart(2, '0')}/${d.monthNumber.toString().padStart(2, '0')}/${d.year}"
                    }
                    showDatePicker = false
                }) { Text("OK", fontWeight = FontWeight.Bold, color = Color(0xFF60A5FA)) }
            },
            dismissButton = {
                TextButton(onClick = { showDatePicker = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            },
            colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B))
        ) { DatePicker(state = datePickerState, colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B), titleContentColor = Color.White, headlineContentColor = Color.White, weekdayContentColor = Color(0xFFA0A0A0), dayContentColor = Color.White, todayContentColor = Color(0xFF60A5FA))) }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EditDebtDialog(
    originalTx: TransactionEntity,
    onDismiss: () -> Unit,
    onSave: (TransactionEntity) -> Unit
) {
    var friendName by remember { mutableStateOf(originalTx.merchantKey ?: "") }

    var amountText by remember { mutableStateOf(
        if (originalTx.amountCents % 100 == 0L) (originalTx.amountCents / 100).toString()
        else "${originalTx.amountCents / 100},${(originalTx.amountCents % 100).toString().padStart(2, '0')}"
    ) }

    var description by remember { mutableStateOf(originalTx.description ?: "") }

    val initialDate = try { LocalDate.fromEpochDays(originalTx.dateEpochDay.toInt()) } catch (e:Exception) { Clock.System.todayIn(TimeZone.currentSystemDefault()) }
    var dateText by remember { mutableStateOf("${initialDate.dayOfMonth.toString().padStart(2, '0')}/${initialDate.monthNumber.toString().padStart(2, '0')}/${initialDate.year}") }
    var showDatePicker by remember { mutableStateOf(false) }
    val datePickerState = rememberDatePickerState()

    Dialog(onDismissRequest = onDismiss, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Surface(
            modifier = Modifier.padding(16.dp).fillMaxWidth().widthIn(max = 400.dp).imePadding(),
            shape = RoundedCornerShape(28.dp),
            color = Color.White.copy(alpha = 0.12f),
            border = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.40f)),
            shadowElevation = 24.dp
        ) {
            Column(modifier = Modifier.padding(24.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                Text("Modifica Movimento", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold), color = Color(0xFF60A5FA))

                OutlinedTextField(
                    value = friendName, onValueChange = { friendName = it },
                    label = { Text("Nome Persona/Ente", color = Color(0xFFA0A0A0)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                OutlinedTextField(
                    value = amountText, onValueChange = { amountText = it.replace(Regex("[^0-9.,]"), "") },
                    label = { Text("Importo (€)", color = Color(0xFFA0A0A0)) }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                    modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                OutlinedTextField(
                    value = dateText,
                    onValueChange = {},
                    readOnly = true,
                    label = { Text("Data (GG/MM/AAAA)", color = Color(0xFFA0A0A0)) },
                    trailingIcon = {
                        IconButton(onClick = { showDatePicker = true }) {
                            Icon(Icons.Default.CalendarMonth, null, tint = Color.White)
                        }
                    },
                    modifier = Modifier.fillMaxWidth().onFocusChanged { state -> if(state.isFocused) showDatePicker = true },
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                OutlinedTextField(
                    value = description, onValueChange = { description = it },
                    label = { Text("Motivo", color = Color(0xFFA0A0A0)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                Row(modifier = Modifier.fillMaxWidth().padding(top = 8.dp), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
                    Spacer(Modifier.width(12.dp))
                    Button(
                        onClick = {
                            val cents = (amountText.replace(",", ".").toDoubleOrNull()?.times(100))?.toLong() ?: 0L

                            val txEpochDay = try {
                                val parts = dateText.split("/")
                                LocalDate(parts[2].toInt(), parts[1].toInt(), parts[0].toInt()).toEpochDays().toLong()
                            } catch (e: Exception) {
                                originalTx.dateEpochDay
                            }

                            if (friendName.isNotBlank() && cents > 0L) {
                                val updatedTx = originalTx.copy(
                                    merchantKey = friendName.uppercase().trim(),
                                    amountCents = cents,
                                    description = description.trim(),
                                    dateEpochDay = txEpochDay,
                                    updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()
                                )
                                onSave(updatedTx)
                            }
                        },
                        shape = RoundedCornerShape(14.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White)
                    ) { Text("SALVA", fontWeight = FontWeight.Bold) }
                }
            }
        }
    }

    if (showDatePicker) {
        DatePickerDialog(
            onDismissRequest = { showDatePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    datePickerState.selectedDateMillis?.let { millis ->
                        val d = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.currentSystemDefault()).date
                        dateText = "${d.dayOfMonth.toString().padStart(2, '0')}/${d.monthNumber.toString().padStart(2, '0')}/${d.year}"
                    }
                    showDatePicker = false
                }) { Text("OK", fontWeight = FontWeight.Bold, color = Color(0xFF60A5FA)) }
            },
            dismissButton = {
                TextButton(onClick = { showDatePicker = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            },
            colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B))
        ) { DatePicker(state = datePickerState, colors = DatePickerDefaults.colors(containerColor = Color(0xFF1E1B4B), titleContentColor = Color.White, headlineContentColor = Color.White, weekdayContentColor = Color(0xFFA0A0A0), dayContentColor = Color.White, todayContentColor = Color(0xFF60A5FA))) }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GlobalEditDebtDialog(
    oldName: String,
    onDismiss: () -> Unit,
    onSave: (newName: String) -> Unit
) {
    var friendName by remember { mutableStateOf(oldName) }

    Dialog(onDismissRequest = onDismiss, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Surface(
            modifier = Modifier.padding(16.dp).fillMaxWidth().widthIn(max = 400.dp).imePadding(),
            shape = RoundedCornerShape(28.dp),
            color = Color.White.copy(alpha = 0.12f),
            border = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.40f)),
            shadowElevation = 24.dp
        ) {
            Column(modifier = Modifier.padding(24.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                Text("Rinomina Persona/Ente", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold), color = Color(0xFF60A5FA))
                Text("Modificando il nome, aggiornerai l'anagrafica di tutti i movimenti collegati, mantenendo assolutamente intatte le causali originali di ogni singolo prestito o piano di rientro.", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))

                OutlinedTextField(
                    value = friendName, onValueChange = { friendName = it },
                    label = { Text("Nome Persona/Ente", color = Color(0xFFA0A0A0)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                Row(modifier = Modifier.fillMaxWidth().padding(top = 8.dp), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
                    Spacer(Modifier.width(12.dp))
                    Button(
                        onClick = {
                            if (friendName.isNotBlank()) {
                                onSave(friendName)
                            }
                        },
                        shape = RoundedCornerShape(14.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White)
                    ) { Text("RINOMINA", fontWeight = FontWeight.Bold) }
                }
            }
        }
    }
}